// HU-29 (MG-ILE-57) · tarea 29-5. El `.xlsx` del maestro se lee EN EL NAVEGADOR y se arma la
// petición compacta del endpoint 41 (AT S3 §2.2.6): los encabezados una vez y cada fila como
// arreglo `[número de fila del archivo, valores en el orden de las columnas]`.
//
// LECTOR: `read-excel-file` (MIT, mantenido, sin dependencias nativas; vacío 9 de la spec). Se
// importa con `import()` para que solo lo descargue quien abre esta pestaña.
//
// Qué NO hace aquí: validar columnas ni filas. Si falta una columna, el servidor rechaza el archivo
// entero con el texto de C2; si una fila no trae nombre o registro, la rechaza sola con su motivo
// (C3, C4). Repetirlo en el cliente sería la segunda copia de la regla (§8.1).
import type { CargaDeMaestro, ValorDeCelda } from '../api/maestro';

/** «.xlsx»: el maestro se carga desde la plantilla (AF §10.3). */
export const esArchivoDelMaestro = (archivo: File): boolean => /\.xlsx$/i.test(archivo.name);

const aCelda = (valor: unknown): ValorDeCelda => {
  if (valor === null || valor === undefined) return null;
  if (typeof valor === 'number') return Number.isInteger(valor) ? valor : String(valor);
  if (typeof valor === 'boolean') return valor ? 'true' : 'false';
  if (valor instanceof Date) return valor.toISOString().slice(0, 10);
  const texto = String(valor).trim();
  return texto || null;
};

const filaVacia = (fila: ValorDeCelda[]): boolean => fila.every((v) => v === null);

/** El archivo como `ArrayBuffer`, con `FileReader` (lo tienen todos los navegadores y jsdom). */
const comoArrayBuffer = (archivo: Blob): Promise<ArrayBuffer> => new Promise((resolver, rechazar) => {
  const lector = new FileReader();
  lector.onerror = () => rechazar(new Error('No se pudo leer el archivo.'));
  lector.onload = () => resolver(lector.result as ArrayBuffer);
  lector.readAsArrayBuffer(archivo);
});

/**
 * Lee la PRIMERA hoja (la plantilla trae «Productos» primero y «Instrucciones» después). La
 * primera fila son los encabezados; cada fila siguiente lleva delante su número en el archivo
 * (la primera de datos es la 2). Las filas completamente vacías no viajan.
 */
export async function leerMaestro(archivo: File): Promise<CargaDeMaestro> {
  const { readSheet } = await import('read-excel-file/browser');
  const datos = await readSheet(await comoArrayBuffer(archivo));
  const [encabezados = [], ...resto] = datos;
  const columnas = encabezados.map((c) => String(aCelda(c) ?? ''));
  const filas: ValorDeCelda[][] = [];
  resto.forEach((fila, i) => {
    const valores = columnas.map((_, j) => aCelda(fila[j]));
    if (!filaVacia(valores)) filas.push([i + 2, ...valores]);
  });
  return { nombre_archivo: archivo.name, columnas, filas };
}
